import { ProductSlugs } from '@deque/billing-utils';
import { apiRequest } from '../../api-client';

export interface ReportRow {
  emailAddress: string;
  firstName: string;
  lastName: string;
  customerName: string;
  role: 'User' | 'Admin';
  product: string;
}

export interface SubscriptionMemberReportResult {
  rows: ReportRow[];
  csvContent: string;
  rowCount: number;
}

/**
 * The subset of ProductSlugs supported by the subscription member report export.
 */
export const EXPORT_PRODUCT_SLUGS = [
  ProductSlugs.axeDevToolsMobile,
  ProductSlugs.axeMonitor,
  ProductSlugs.axeAuditor,
  ProductSlugs.axeDevToolsExtension,
  ProductSlugs.axeDevToolsHTML
] as const;

export type ExportSlug = (typeof EXPORT_PRODUCT_SLUGS)[number];

/**
 * Products that support group-based filtering (includeUsers / includeAdmins).
 */
export const GROUP_PRODUCT_SLUGS: ReadonlySet<ExportSlug> = new Set<ExportSlug>(
  [
    ProductSlugs.axeDevToolsMobile,
    ProductSlugs.axeMonitor,
    ProductSlugs.axeAuditor
  ]
);

interface GetSubscriptionMemberReportParams {
  token: string;
  product: ExportSlug;
  includeUsers?: boolean;
  includeAdmins?: boolean;
}

/**
 * Generates a subscription member report for a given product.
 *
 * GET /api-internal/subscription-member-report?product=<slug>&includeUsers=<bool>&includeAdmins=<bool>
 */
export function getSubscriptionMemberReport({
  token,
  product,
  includeUsers = true,
  includeAdmins = true
}: GetSubscriptionMemberReportParams): Promise<SubscriptionMemberReportResult> {
  const params = new URLSearchParams({ product });
  if (!includeUsers) {
    params.set('includeUsers', 'false');
  }
  if (!includeAdmins) {
    params.set('includeAdmins', 'false');
  }

  return apiRequest<SubscriptionMemberReportResult>({
    method: 'GET',
    endpoint: `/api-internal/subscription-member-report?${params}`,
    token
  });
}
